import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardHero, ArticleCardStandard, ArticleCardCompact, ArticleCardSidebar } from '../components/cards/ArticleCard';
import AuthModal from '../components/modals/AuthModal';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { TrendingUp, Sparkles, Clock, ChevronRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { slug: 'news', name: 'News' },
  { slug: 'personal-finance', name: 'Personal Finance' },
  { slug: 'debt-management', name: 'Debt Management' },
  { slug: 'small-business', name: 'Small Business' },
  { slug: 'investing', name: 'Investing' },
  { slug: 'markets', name: 'Markets' },
];

export default function HomePage() {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState({});
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    fetchAllContent();
  }, []);

  const fetchAllContent = async () => {
    try {
      const [featuredRes, latestRes, trendingRes] = await Promise.all([
        axios.get(`${API}/articles/featured`, { params: { limit: 5 } }),
        axios.get(`${API}/articles`, { params: { limit: 12 } }),
        axios.get(`${API}/articles/trending`, { params: { limit: 5 } }),
      ]);

      setFeaturedArticles(featuredRes.data);
      setLatestArticles(latestRes.data);
      setTrendingArticles(trendingRes.data);

      const categoryData = {};
      for (const cat of categories.slice(0, 4)) {
        try {
          const res = await axios.get(`${API}/articles`, { params: { category: cat.slug, limit: 4 } });
          categoryData[cat.slug] = res.data;
        } catch (e) {
          categoryData[cat.slug] = [];
        }
      }
      setCategoryArticles(categoryData);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <StockTicker />
      <Navbar />

      {/* Hero Section */}
      <section className="hero-section border-b-2 border-black" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-8 lg:border-r-2 lg:border-black">
                <Skeleton className="w-full h-[280px] sm:h-[400px] lg:h-[500px]" />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1">
                <Skeleton className="w-full h-[140px] sm:h-[180px] lg:h-[250px]" />
                <Skeleton className="w-full h-[140px] sm:h-[180px] lg:h-[250px]" />
              </div>
            </div>
          ) : featuredArticles.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-8 lg:border-r-2 lg:border-black">
                <ArticleCardHero article={featuredArticles[0] || latestArticles[0]} />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 divide-x-2 lg:divide-x-0 lg:divide-y-2 divide-black">
                {(featuredArticles.slice(1, 3).length > 0 ? featuredArticles.slice(1, 3) : latestArticles.slice(1, 3)).map((article) => (
                  <ArticleCardHero key={article.id} article={article} />
                ))}
              </div>
            </div>
          ) : latestArticles.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-8 lg:border-r-2 lg:border-black">
                <ArticleCardHero article={latestArticles[0]} />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 divide-x-2 lg:divide-x-0 lg:divide-y-2 divide-black">
                {latestArticles.slice(1, 3).map((article) => (
                  <ArticleCardHero key={article.id} article={article} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-zinc-400 font-mono text-sm uppercase tracking-wider">No articles available yet. Content is being fetched...</p>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 sm:py-16" data-testid="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12">
            {/* Articles Grid */}
            <div className="lg:col-span-8">
              {/* Latest News */}
              <div className="mb-10 sm:mb-16">
                <div className="flex items-center justify-between mb-6 sm:mb-8 border-b-3 border-black pb-3">
                  <h2 className="flex items-center text-lg sm:text-2xl font-black text-black tracking-tight" style={{ fontFamily: "'Merriweather', serif" }}>
                    <Clock className="w-5 sm:w-6 h-5 sm:h-6 mr-2 text-red-600" strokeWidth={2} />
                    Latest News
                  </h2>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Updated Live</span>
                </div>
                {loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {[...Array(4)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[250px] sm:h-[300px]" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {latestArticles.slice(0, 6).map((article) => (
                      <ArticleCardStandard key={article.id} article={article} />
                    ))}
                  </div>
                )}
              </div>

              {/* Category Sections */}
              {categories.slice(0, 3).map((cat) => (
                <div key={cat.slug} className="mb-10 sm:mb-16">
                  <div className="flex items-center justify-between mb-6 sm:mb-8 border-b-3 border-black pb-3">
                    <h2 className="text-base sm:text-xl font-black text-black tracking-tight" style={{ fontFamily: "'Merriweather', serif" }}>
                      {cat.name}
                    </h2>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="flex items-center text-xs font-mono font-bold text-red-600 hover:text-black transition-colors uppercase tracking-widest"
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2} />
                    </Link>
                  </div>
                  {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {[...Array(2)].map((_, i) => (
                        <Skeleton key={i} className="w-full h-[280px]" />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {(categoryArticles[cat.slug] || latestArticles.filter(a => a.category === cat.slug)).slice(0, 4).map((article) => (
                        <ArticleCardStandard key={article.id} article={article} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4 lg:border-l lg:border-zinc-200 lg:pl-8 space-y-8 sm:space-y-10">
              {/* Newsletter Widget */}
              <div className="border-2 border-black p-5 sm:p-6">
                <h3 className="font-mono font-bold text-xs uppercase tracking-widest border-b-2 border-black pb-2 mb-4 text-black">
                  Stay Updated
                </h3>
                <p className="text-zinc-600 text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Get the latest financial news and insights delivered to your inbox.
                </p>
                <Button
                  onClick={() => setAuthModalOpen(true)}
                  className="w-full bg-black text-white hover:bg-red-600 font-mono font-bold uppercase tracking-widest text-xs rounded-none border-0"
                  data-testid="subscribe-btn"
                >
                  <Mail className="w-4 h-4 mr-2" strokeWidth={2} />
                  Subscribe
                </Button>
              </div>

              {/* Trending */}
              <div className="border-2 border-black p-5 sm:p-6">
                <h3 className="font-mono font-bold text-xs uppercase tracking-widest border-b-2 border-black pb-2 mb-4 flex items-center text-black">
                  <TrendingUp className="w-4 h-4 mr-2 text-red-600" strokeWidth={2} />
                  Trending
                </h3>
                <div className="space-y-0">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[60px] mb-4" />
                    ))
                  ) : trendingArticles.length > 0 ? (
                    trendingArticles.map((article, index) => (
                      <ArticleCardCompact key={article.id} article={article} index={index} />
                    ))
                  ) : latestArticles.slice(0, 5).map((article, index) => (
                    <ArticleCardCompact key={article.id} article={article} index={index} />
                  ))}
                </div>
              </div>

              {/* Editor's Picks */}
              <div className="border-2 border-black p-5 sm:p-6">
                <h3 className="font-mono font-bold text-xs uppercase tracking-widest border-b-2 border-black pb-2 mb-4 flex items-center text-black">
                  <Sparkles className="w-4 h-4 mr-2 text-red-600" strokeWidth={2} />
                  Editor's Picks
                </h3>
                <div className="space-y-4">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[70px] sm:h-[80px]" />
                    ))
                  ) : (featuredArticles.length > 0 ? featuredArticles : latestArticles).slice(0, 5).map((article) => (
                    <ArticleCardSidebar key={article.id} article={article} />
                  ))}
                </div>
              </div>

              {/* Advertise CTA */}
              <div className="border-2 border-red-600 p-5 sm:p-6 bg-red-600/5">
                <h3 className="font-mono font-bold text-xs uppercase tracking-widest text-red-600 mb-2">
                  Advertise With Us
                </h3>
                <p className="text-zinc-600 text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Reach thousands of finance-focused readers with your message.
                </p>
                <Link
                  to="/advertise"
                  className="inline-block bg-red-600 text-white px-5 py-2 text-xs font-mono font-bold uppercase tracking-widest hover:bg-black transition-colors"
                  data-testid="advertise-cta"
                >
                  Learn More
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode="register" />
    </div>
  );
}
